import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  untracked
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { AppLoaderComponent } from '@view/shared/molecules/loader/loader.component';
import { LoaderType } from '@helpers/html/loader-type';
import { ClipboardUtils } from '@common/clipboard-utils';
import { LoadStatus, ReportLoad } from '@model/report-load/report-load.model';
import { AppTranslationPipe } from '@adapter/i18n/app-translation.pipe';

@Component({
  selector: 'app-report-table',
  imports: [
    CommonModule,
    PaginatorModule,
    AppLoaderComponent,
    AppTranslationPipe
  ],
  templateUrl: './report-table.component.html',
  styleUrl: './report-table.component.scss'
})
export class ReportTableComponent {
  private readonly elRef = inject(ElementRef);
  protected readonly LoaderType = LoaderType;
  protected readonly LoadStatus = LoadStatus;

  loads = input<ReportLoad[]>([]);
  isLoading = input<boolean>(false);
  isRefreshing = input<boolean>(false);
  deletingLoadId = input<string | null>(null);
  rowsPerPage = input<number>(5);
  currentPage = input<number>(0);
  totalRecords = input<number>(0);
  rowsPerPageOptions = input<number[]>([5, 10, 20]);

  copied = signal<string | null>(null);
  expandedRowId = signal<string | null>(null);
  skeletonCount = computed(() =>
    this.loads().length > 0 ? this.loads().length : this.rowsPerPage()
  );

  constructor() {
    effect(() => {
      this.loads();
      untracked(() => this.expandedRowId.set(null));
    });
  }

  getProgress(load: {
    processedRecords: number;
    totalRecords: number;
  }): number {
    if (!load.totalRecords || load.totalRecords <= 0) return 0;
    return Math.min(
      Math.round((load.processedRecords / load.totalRecords) * 100),
      100
    );
  }

  pageChange = output<PaginatorState>();
  deleteLoad = output<ReportLoad>();

  toggleErrorRow(loadId: string): void {
    this.expandedRowId.update((current) =>
      current === loadId ? null : loadId
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Node;
    if (
      this.expandedRowId() !== null &&
      !(this.elRef.nativeElement as HTMLElement).contains(target)
    ) {
      this.expandedRowId.set(null);
    }
  }

  copyField(value: string): void {
    ClipboardUtils.copyToClipboard(value, () => {
      this.copied.set(value);
      setTimeout(() => this.copied.set(null), 3000);
    });
  }
}
